import { Paper, PaperContent } from '@e3dc-react/shell/Elements/Paper'
import React, { useCallback, useEffect } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Table } from '@e3dc-react/shell/Elements/Table'
import { ApiTableSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { Box, Button, Stack } from '@mui/material'
import { J2Add } from '@e3dc-react/icons'
import { useCloudKeysTable, useInstallationAccessTable } from 'src/Libs/installation-rights-management/hooks'
import useCloudKeys from 'src/Libs/installation-rights-management/hooks/use-cloud-keys'
import { makeStyles } from 'tss-react/mui'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { RIGHTS_ADMINISTRATION_KEYS_ID } from 'src/Libs/qa/ids'
import { BlankView } from 'src/Components/blank-view'

const useStyles = makeStyles()(() => ({
  row: {
    '& .MuiTableRow-root': {
      'td:nth-of-type(2)': {
        width: '50%',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        wordBreak: 'break-all',
        hyphens: 'auto',
      },
    },
  },
}))

interface Props {
  installation: Installation
  setCloudKeys: (cloudKeys: React.ReactNode[][]) => void
}

const CloudRegistryKeysView: React.FC<Props> = ({ installation, setCloudKeys }) => {
  const { t } = useCustomTranslation()
  const { header, data, refreshData, actionMenus, isLoading } = useCloudKeysTable(installation)
  const { accountLinks } = useInstallationAccessTable(installation)
  const { createNewKeyDialog } = useCloudKeys()

  /* changing account links can have side effects on CRK. e.g. if an account link is deleted the CRK must be deleted as well */
  useEffect(() => {
    if (installation) {
      refreshData().catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountLinks])

  useEffect(() => {
    setCloudKeys(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length])

  const handleGenerateNewKeyDialog = useCallback((): void => {
    if (installation) {
      createNewKeyDialog(installation).then(() => {
        refreshData().catch(console.error)
      })
    } else {
      console.error('Missing installation')
    }
  }, [createNewKeyDialog, installation, refreshData])

  return (
    <Paper title={t('installation.cloud-registry-keys.title')} dataQa={RIGHTS_ADMINISTRATION_KEYS_ID}>
      <PaperContent>
        <PaperContentTable isLoading={isLoading} data={data} header={header} actionMenus={actionMenus} onCreateNewKey={handleGenerateNewKeyDialog} />
      </PaperContent>
    </Paper>
  )
}

export default CloudRegistryKeysView

// #region PaperContentTable
interface PaperContentTableProps {
  isLoading: boolean
  data: React.ReactNode[][]
  header: React.ComponentProps<typeof Table>['header']
  actionMenus: React.ReactNode[]
  onCreateNewKey: () => void
}

const PaperContentTable: React.FC<PaperContentTableProps> = ({ isLoading, data, header, actionMenus, onCreateNewKey }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const renderTable = (): React.ReactNode => {
    if (isLoading) {
      return <ApiTableSkeleton withFilter={false} rowAmount={2} />
    }

    if (data.length === 0) {
      return (
        <BlankView
          title={t('installation.cloud-registry-keys.no-data-title')}
          explanation={t('installation.cloud-registry-keys.no-data-subtitle')}
          imageVariant={'NEUTRAL'}
        />
      )
    }

    return (
      <Table
        data={data}
        hasColoredTableColumns
        header={header}
        bodyElevation={0}
        footerElevation={0}
        headerElevation={0}
        customFunctionalities={[actionMenus]}
        muiTableClassName={classes.row}
      />
    )
  }

  return (
    <>
      {renderTable()}
      <Box sx={{ mt: 5 }}>
        <Stack direction={'row'}>
          <Button onClick={onCreateNewKey} variant={'outlined'} startIcon={<J2Add />}>
            {t('installation.cloud-registry-keys.controls.create-new')}
          </Button>
        </Stack>
      </Box>
    </>
  )
}
// #endregion
